import { Modal, Form, Input, Switch, Button, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { TSmartVariableManagerProps } from '@/types/TSmartCRM';
import { FieldOptionsEditor } from './FieldOptionsEditor';
import { EFieldType } from 'src/enums/ESmartCRM';
import {
	FIELD_TYPE_CONFIGS,
	FORM_VALIDATION_RULES,
	MODAL_CONFIGS,
	PLACEHOLDERS,
} from 'src/constants/smartCRM';
import { generateInternalKey } from '@/utils/SmartCRM/fieldUtils';

const { TextArea } = Input;
const { Option } = Select;

interface FormValues {
	name: string;
	internalKey: string;
	type: EFieldType;
	description?: string;
	required: boolean;
	options?: string[];
}

const countWords = (text: string): number => {
	return text
		.trim()
		.split(/\s+/)
		.filter((word) => word.length > 0).length;
};

export const SmartVariableManager = ({
	visible,
	initialData,
	onSave,
	onCancel,
}: TSmartVariableManagerProps) => {
	const [form] = Form.useForm<FormValues>();
	const [selectedType, setSelectedType] = useState<EFieldType>(EFieldType.TEXT);
	const [loading, setLoading] = useState<boolean>(false);
	const [descriptionWordCount, setDescriptionWordCount] = useState<number>(0);

	useEffect(() => {
		if (visible) {
			if (initialData) {
				const formValues = {
					name: initialData.fieldName,
					internalKey: initialData.internalKey,
					type: initialData.fieldType,
					description: initialData.description,
					required: initialData.isRequired,
					options: initialData.options,
				};
				form.setFieldsValue(formValues);
				setSelectedType(initialData.fieldType);
				setDescriptionWordCount(countWords(initialData.description || ''));
			} else {
				form.resetFields();
				setSelectedType(EFieldType.TEXT);
				setDescriptionWordCount(0);
			}
		}
	}, [visible, initialData, form]);

	const handleFieldTypeChange = (type: EFieldType): void => {
		setSelectedType(type);
		form.setFieldValue('type', type);

		if (!FIELD_TYPE_CONFIGS[type].hasOptions) {
			form.setFieldValue('options', undefined);
		}
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const name = e.target.value;
		if (!isEditing) {
			const internalKey = generateInternalKey(name);
			form.setFieldValue('internalKey', internalKey);
		}
	};

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
		const text = e.target.value;
		const wordCount = countWords(text);
		setDescriptionWordCount(wordCount);
	};

	const handleSubmit = async (values: FormValues): Promise<void> => {
		setLoading(true);

		const backendPayload = {
			fieldName: values.name,
			internalKey: values.internalKey,
			fieldType: values.type || selectedType,
			description: values.description,
			isRequired: values.required,
			options: FIELD_TYPE_CONFIGS[values.type || selectedType]?.hasOptions
				? values.options
				: undefined,
			...(initialData?.id && { id: initialData.id }),
		};

		await onSave(backendPayload);
		setLoading(false);
	};

	const handleCancel = (): void => {
		if (!loading) {
			onCancel();
		}
	};

	const fieldTypeConfig = FIELD_TYPE_CONFIGS[selectedType];
	const isEditing = Boolean(initialData);

	const fieldTypeOptions = Object.entries(FIELD_TYPE_CONFIGS)
		.filter(([key]) => key !== 'ARRAY')
		.map(([key, config]) => ({
			value: key as EFieldType,
			label: config.label,
			description: config.description,
		}));

	return (
		<Modal
			title={isEditing ? 'Edit Customer Info Field' : 'Create Customer Info Field'}
			open={visible}
			onCancel={handleCancel}
			width={MODAL_CONFIGS.FIELD_MANAGER.width}
			centered={MODAL_CONFIGS.FIELD_MANAGER.centered}
			maskClosable={!loading && MODAL_CONFIGS.FIELD_MANAGER.maskClosable}
			destroyOnClose={MODAL_CONFIGS.FIELD_MANAGER.destroyOnClose}
			footer={null}
			closable={!loading}
		>
			<div className="space-y-6">
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
					<div className="flex items-start space-x-3">
						<InfoCircleOutlined className="text-blue-500 mt-0.5" />
						<div>
							<p className="font-medium text-blue-900 mb-1">💡 Tip</p>
							<p className="text-blue-700 text-sm">
								Think about what information would help you provide better service. Examples:
								preferred appointment times, vehicle details, special requests, or customer
								preferences.
							</p>
						</div>
					</div>
				</div>

				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{
						required: false,
						type: EFieldType.TEXT,
					}}
				>
					<Form.Item
						name="name"
						label="Field Name"
						rules={[
							{
								required: FORM_VALIDATION_RULES.FIELD_NAME.required,
								message: FORM_VALIDATION_RULES.FIELD_NAME.message,
							},
							{
								min: FORM_VALIDATION_RULES.FIELD_NAME.min,
								message: FORM_VALIDATION_RULES.FIELD_NAME.minMessage,
							},
						]}
					>
						<Input
							placeholder={PLACEHOLDERS.FIELD_NAME}
							onChange={handleNameChange}
							disabled={loading}
						/>
					</Form.Item>

					<Form.Item
						name="internalKey"
						label="Internal Key"
						rules={[
							{
								required: FORM_VALIDATION_RULES.INTERNAL_KEY.required,
								message: FORM_VALIDATION_RULES.INTERNAL_KEY.message,
							},
							{
								pattern: FORM_VALIDATION_RULES.INTERNAL_KEY.pattern,
								message: FORM_VALIDATION_RULES.INTERNAL_KEY.patternMessage,
							},
						]}
					>
						<Input placeholder={PLACEHOLDERS.INTERNAL_KEY} disabled={isEditing || loading} />
					</Form.Item>

					<Form.Item
						name="type"
						label="Field Type"
						rules={[
							{
								required: FORM_VALIDATION_RULES.FIELD_TYPE.required,
								message: FORM_VALIDATION_RULES.FIELD_TYPE.message,
							},
						]}
					>
						<Select
							value={selectedType}
							onChange={handleFieldTypeChange}
							placeholder={PLACEHOLDERS.SELECT_FIELD_TYPE}
							size="large"
							disabled={isEditing || loading}
							optionLabelProp="label"
						>
							{fieldTypeOptions.map((option) => (
								<Option key={option.value} value={option.value}>
									<div>
										<div className="font-medium">{option.label}</div>
										<div className="text-xs text-gray-500">{option.description}</div>
									</div>
								</Option>
							))}
						</Select>
					</Form.Item>

					{fieldTypeConfig?.hasOptions && (
						<Form.Item
							name="options"
							label="Options"
							rules={[
								{
									required: FORM_VALIDATION_RULES.OPTIONS.required,
									message: FORM_VALIDATION_RULES.OPTIONS.message,
								},
							]}
						>
							<FieldOptionsEditor
								value={form.getFieldValue('options') || []}
								onChange={(options) => form.setFieldValue('options', options)}
								disabled={loading}
							/>
						</Form.Item>
					)}

					<Form.Item
						name="description"
						label={
							<span>
								Scenario{' '}
								<Tooltip title="Describe the purpose of this field so AI receptionist can understand what information to collect for the leads.">
									<InfoCircleOutlined className="text-gray-400" />
								</Tooltip>
							</span>
						}
						rules={[
							{
								required: true,
								message: 'Please provide a scenario for this field',
							},
							{
								validator: (_, value) => {
									if (value && countWords(value) > 100) {
										return Promise.reject(new Error('Description cannot exceed 100 words'));
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<div>
							<TextArea
								placeholder="Describe when and how this field should be used (e.g., 'Use this to track customer preferences during sales calls')"
								rows={3}
								disabled={loading}
								defaultValue={initialData ? initialData.description : undefined}
								onChange={handleDescriptionChange}
								maxLength={1000}
							/>
							<div className="flex justify-between items-center mt-1">
								<div className="text-xs text-gray-500">Maximum 100 words allowed</div>
								<div
									className={`text-xs ${descriptionWordCount > 100 ? 'text-red-500' : 'text-gray-500'}`}
								>
									{descriptionWordCount}/100 words
								</div>
							</div>
						</div>
					</Form.Item>

					<div className="border-t pt-4">
						<Form.Item name="required" valuePropName="checked" className="mb-4">
							<Switch disabled={loading} />
						</Form.Item>
						<div className="flex items-center space-x-2 -mt-2">
							<span>Make this field required</span>
						</div>
						<p className="text-xs text-gray-500 mt-1">
							If checked, this field must be filled out for every customer
						</p>
					</div>

					<div className="flex justify-end space-x-3 pt-4 border-t">
						<Button onClick={handleCancel} disabled={loading}>
							Cancel
						</Button>
						<Button type="primary" loading={loading} onClick={() => form.submit()}>
							{isEditing ? 'Update Field' : 'Create Field'}
						</Button>
					</div>
				</Form>
			</div>
		</Modal>
	);
};
